// 관리자 콘솔 API 호출 단일 진입점.
// 사용자 앱 클라이언트와 같은 구조 + 401 자동 refresh 인터셉터.

import { useAdminAuthStore } from "@/stores/adminAuthStore.ts";

const API_BASE = import.meta.env.VITE_API_BASE;

// 서버 표준 에러 응답을 클라이언트 예외로 정규화한다 (docs/api-spec.md §2).
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface ApiRequestInit extends Omit<RequestInit, "body" | "headers"> {
  body?: unknown;
  headers?: Record<string, string>;
  withAuth?: boolean;
  autoRefresh?: boolean;
}

// 동시 다발 401 요청들이 각자 refresh 를 호출해 thundering herd 가 되지 않도록 단일 promise 를 공유한다.
let refreshInFlight: Promise<boolean> | null = null;

// 리프레시 토큰으로 새 액세스를 받아 store 에 반영한다.
async function ensureRefreshedOnce(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    const store = useAdminAuthStore.getState();
    const session = store.session;
    if (!session) {
      return false;
    }
    const refreshToken = (session as { refreshToken?: string }).refreshToken;
    if (!refreshToken) {
      store.clearSession();
      return false;
    }
    try {
      const res = await fetch(`${API_BASE}/admin/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        // 서버가 "토큰 무효(401/403)"라고 명확히 답한 경우에만 로그아웃한다.
        // 5xx 등 일시 장애로 세션을 지우면 멀쩡한 로그인이 풀린다(사용자 앱과 동일 기준).
        if (res.status === 401 || res.status === 403) {
          store.clearSession();
        }
        return false;
      }
      const data = (await res.json()) as { accessToken: string; expiresAtSec: number };
      store.setSession({
        ...session,
        accessToken: data.accessToken,
        expiresAtSec: data.expiresAtSec,
      });
      return true;
    } catch {
      // 네트워크 오류는 토큰 무효가 아니다. 세션을 유지하고 이번 요청만 실패시킨다(다음 요청에서 재시도).
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function executeOnce(
  path: string,
  init: ApiRequestInit | undefined,
  withAuth: boolean,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init?.headers ?? {}),
  };
  if (init?.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (withAuth) {
    const token = useAdminAuthStore.getState().session?.accessToken;
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
}

// 단일 진입 fetch 래퍼.
// 401(AUTH_REQUIRED/AUTH_INVALID) 응답을 받으면 한 번에 한해 자동으로 refresh 후 재시도한다.
export async function apiRequest<T>(path: string, init?: ApiRequestInit): Promise<T> {
  const withAuth = init?.withAuth ?? true;
  const autoRefresh = init?.autoRefresh ?? true;

  let res = await executeOnce(path, init, withAuth);

  if (res.status === 401 && withAuth && autoRefresh) {
    const refreshed = await ensureRefreshedOnce();
    if (refreshed) {
      res = await executeOnce(path, init, withAuth);
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { ok: false, code: "INTERNAL", message: text.slice(0, 200) };
    }
  }

  if (!res.ok) {
    const obj = (payload ?? {}) as { code?: string; message?: string };
    throw new ApiError(
      res.status,
      obj.code ?? "INTERNAL",
      obj.message ?? "요청 처리에 실패했습니다.",
    );
  }
  return payload as T;
}

export const api = {
  get<T>(path: string, init?: Omit<ApiRequestInit, "method" | "body">) {
    return apiRequest<T>(path, { ...init, method: "GET" });
  },
  post<T>(path: string, body?: unknown, init?: Omit<ApiRequestInit, "method" | "body">) {
    return apiRequest<T>(path, { ...init, method: "POST", body });
  },
  patch<T>(path: string, body?: unknown, init?: Omit<ApiRequestInit, "method" | "body">) {
    return apiRequest<T>(path, { ...init, method: "PATCH", body });
  },
  delete<T>(path: string, init?: Omit<ApiRequestInit, "method" | "body">) {
    return apiRequest<T>(path, { ...init, method: "DELETE" });
  },
};
